import React, { useEffect, useState } from 'react';
import { ExtendedSize, DpiWarning as DpiWarningType } from './types';
import DpiWarning from './DpiWarning';
import api from '../../services/api';
import { trackOptionEvent } from '../../services/metrics';

interface SizeSelectionStepProps {
  sizes: ExtendedSize[];
  loadingSizes: boolean;
  selectedSizeId: number | null;
  selectedAspectRatio: number;
  completedCrop: any; // PixelCrop
  imgRef?: React.RefObject<HTMLImageElement>;
  displayedImageDimensions?: { width: number; height: number } | null;
  naturalImageDimensions?: { width: number; height: number } | null;
  calculatedDpi: number | null;
  dpiWarning: DpiWarningType | null;
  onSizeSelect: (sizeId: number, sizeValue: string) => void;
  onDpiCalculated?: (dpi: number | null) => void;
  onDpiWarning?: (warning: DpiWarningType | null) => void;
}

const SizeSelectionStep: React.FC<SizeSelectionStepProps> = ({
  sizes,
  loadingSizes,
  selectedSizeId,
  selectedAspectRatio,
  completedCrop,
  imgRef,
  displayedImageDimensions,
  naturalImageDimensions,
  calculatedDpi,
  dpiWarning,
  onSizeSelect,
  onDpiCalculated,
  onDpiWarning
}) => {
  const [sizeDpiMap, setSizeDpiMap] = useState<Map<number, number>>(new Map());
  const [sizeInventoryMap, setSizeInventoryMap] = useState<Map<number, { available: number; isBackorder: boolean; expectedDate?: Date }>>(new Map());

  // Filter sizes to match the selected aspect ratio (with tolerance)
  // Only show sizes where the portrait orientation matches the selected aspect ratio
  // Do NOT show the inverse (landscape) orientation - e.g., if 4:3 is selected, show 8x6 but not 6x8
  const matchingSizes = sizes.filter(size => {
    const portraitRatio = size.portrait.aspectRatio;
    const tolerance = 0.05; // 5% tolerance for aspect ratio matching
    
    // Only match if the portrait aspect ratio matches (not the inverse)
    return Math.abs(portraitRatio - selectedAspectRatio) < tolerance;
  });

  // Calculate DPI for each size when crop is available
  useEffect(() => {
    if (!completedCrop) return;

    let naturalWidth: number | undefined;
    let naturalHeight: number | undefined;
    let displayedWidth: number | undefined;
    let displayedHeight: number | undefined;

    if (imgRef?.current) {
      const imgElement = imgRef.current;
      naturalWidth = imgElement.naturalWidth || imgElement.width;
      naturalHeight = imgElement.naturalHeight || imgElement.height;
      displayedWidth = imgElement.offsetWidth || imgElement.width || naturalWidth;
      displayedHeight = imgElement.offsetHeight || imgElement.height || naturalHeight;
    } else if (displayedImageDimensions && naturalImageDimensions) {
      displayedWidth = displayedImageDimensions.width;
      displayedHeight = displayedImageDimensions.height;
      naturalWidth = naturalImageDimensions.width;
      naturalHeight = naturalImageDimensions.height;
    }

    if (!naturalWidth || !naturalHeight || !displayedWidth || !displayedHeight) return;
    if (naturalWidth <= 0 || naturalHeight <= 0 || displayedWidth <= 0 || displayedHeight <= 0) return;

    const scaleX = naturalWidth / displayedWidth;
    const scaleY = naturalHeight / displayedHeight;
    const cropWidthPixels = completedCrop.width * scaleX;
    const cropHeightPixels = completedCrop.height * scaleY;

    const newSizeDpiMap = new Map<number, number>();

    matchingSizes.forEach(size => {
      // Try portrait first
      const sizeParts = size.portrait.value.split('x');
      if (sizeParts.length === 2) {
        const printWidthInches = parseFloat(sizeParts[0]);
        const printHeightInches = parseFloat(sizeParts[1]);
        
        if (!isNaN(printWidthInches) && !isNaN(printHeightInches)) {
          const dpiWidth = cropWidthPixels / printWidthInches;
          const dpiHeight = cropHeightPixels / printHeightInches;
          const dpi = Math.min(dpiWidth, dpiHeight);
          newSizeDpiMap.set(size.id, dpi);
        }
      }
    });

    setSizeDpiMap(newSizeDpiMap);

    // Calculate DPI for selected size if available
    if (selectedSizeId && newSizeDpiMap.has(selectedSizeId)) {
      const dpi = newSizeDpiMap.get(selectedSizeId)!;
      if (onDpiCalculated) onDpiCalculated(dpi);
      
      if (onDpiWarning) {
        if (dpi < 75) {
          onDpiWarning({
            level: 'error',
            message: `Low resolution (${dpi.toFixed(0)} DPI). Image will appear pixelated.`
          });
        } else if (dpi < 150) {
          onDpiWarning({
            level: 'warning',
            message: `Moderate resolution (${dpi.toFixed(0)} DPI). For best quality, we recommend at least 150 DPI.`
          });
        } else if (dpi < 300) {
          onDpiWarning({
            level: 'info',
            message: `Good resolution (${dpi.toFixed(0)} DPI). Image quality should be acceptable.`
          });
        } else {
          onDpiWarning({
            level: 'success',
            message: `High resolution (${dpi.toFixed(0)} DPI). Excellent quality!`
          });
        }
      }
    }
  }, [completedCrop, matchingSizes, selectedSizeId, imgRef, displayedImageDimensions, naturalImageDimensions, onDpiCalculated, onDpiWarning]);

  // Check inventory for each size with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const checkInventory = async () => {
        const inventoryMap = new Map<number, { available: number; isBackorder: boolean; expectedDate?: Date }>();
        
        for (const size of matchingSizes) {
          try {
            // For custom prints, check if we can make this size from stretcher bars
            const sizeValue = size.portrait.value; // e.g., "10x20" or "5x10"
            
            // Call backend to check availability for this size
            const response = await api.get<{ available: number }>(
              `/inventory/check-size?size=${encodeURIComponent(sizeValue)}`
            );
            
            const available = response.data?.available ?? 0;
            const isBackorder = available === 0; // If we can't make it from bars, it's backorder
            
            // Calculate expected date for backorder (2 weeks from now for custom prints)
            const expectedDate = isBackorder ? new Date(Date.now() + 14 * 24 * 60 * 60 * 1000) : undefined;
            
            inventoryMap.set(size.id, { available, isBackorder, expectedDate });
          } catch (error) {
            console.error(`[Inventory] Error checking size ${size.portrait.value}:`, error);
            // If API call fails, assume backorder with 2 week lead time
            const expectedDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
            inventoryMap.set(size.id, { available: 0, isBackorder: true, expectedDate });
          }
        }
        
        setSizeInventoryMap(inventoryMap);
      };
      
      if (matchingSizes.length > 0) {
        checkInventory();
      }
    }, 500); // Debounce for 500ms
    
    return () => clearTimeout(timeoutId);
  }, [matchingSizes]);

  const getDpiBadge = (dpi: number | undefined): { text: string; className: string } => {
    if (!dpi) return { text: '', className: '' };
    if (dpi >= 300) return { text: `Excellent (${dpi.toFixed(0)} DPI)`, className: 'dpi-excellent' };
    if (dpi >= 150) return { text: `Good (${dpi.toFixed(0)} DPI)`, className: 'dpi-good' };
    if (dpi >= 75) return { text: `Fair (${dpi.toFixed(0)} DPI)`, className: 'dpi-fair' };
    return { text: `Low (${dpi.toFixed(0)} DPI)`, className: 'dpi-low' };
  };

  // Calculate backorder date for a specific size
  const getBackorderDate = (sizeId: number): string => {
    const inventory = sizeInventoryMap.get(sizeId);
    if (inventory?.expectedDate) {
      return inventory.expectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    // Default to 2 weeks from now if no date set
    const defaultDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    return defaultDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="wizard-step size-selection-step">
      <h2>Choose Your Print Size</h2>
      <p className="step-description">
        Select the size for your custom print. Sizes are filtered to match your crop aspect ratio.
        {completedCrop && (
          <span className="dpi-info-hint">
            <i className="fas fa-info-circle"></i> DPI recommendations are based on your cropped image.
          </span>
        )}
      </p>

      {dpiWarning && selectedSizeId && (
        <div style={{ marginBottom: '20px' }}>
          <DpiWarning warning={dpiWarning} calculatedDpi={calculatedDpi} />
        </div>
      )}

      {loadingSizes ? (
        <div className="loading">Loading sizes...</div>
      ) : matchingSizes.length === 0 ? (
        <div className="error-text">
          <p>No sizes available for aspect ratio {selectedAspectRatio.toFixed(2)}:1</p>
          <p>Please go back and choose a different aspect ratio, or contact support to add sizes for this aspect ratio.</p>
        </div>
      ) : (
        <div className="all-sizes-section">
          <h3>Available Sizes</h3>
          {completedCrop && (
            <p className="recommendation-note" style={{ marginBottom: '20px', fontSize: '14px', color: 'var(--text-secondary)' }}>
              <i className="fas fa-star" style={{ color: 'var(--color-teal)' }}></i> We highlight the best DPI size; <strong>150+ DPI</strong> is ideal
            </p>
          )}
          <div className="sizes-grid">
            {matchingSizes.map((size) => {
              const dpi = sizeDpiMap.get(size.id);
              const badge = getDpiBadge(dpi);
              const bestDpi = Math.max(0, ...matchingSizes.map(s => sizeDpiMap.get(s.id) || 0));
              const isRecommended = !!dpi && (dpi >= 150 || (bestDpi > 0 && dpi === bestDpi));
              const isLowQuality = dpi && dpi < 150;
              const inventory = sizeInventoryMap.get(size.id);
              const isBackorder = inventory?.isBackorder ?? true;
              
              return (
                    <button
                  key={size.id}
                  type="button"
                  className={`size-option ${selectedSizeId === size.id ? 'selected' : ''} ${badge.className} ${isLowQuality ? 'low-quality' : ''} ${isRecommended ? 'recommended' : ''}`}
                  onClick={() => onSizeSelect(size.id, size.portrait.value)}
                      onMouseEnter={() => {
                        trackOptionEvent({
                          itemType: 'CustomPrint',
                          size: size.portrait.value,
                          eventType: 'Viewed'
                        });
                      }}
                >
                  {isRecommended && (
                    <div className="recommended-badge">
                      <i className="fas fa-star"></i> Recommended
                    </div>
                  )}
                  <div className="size-label">{size.portrait.label}</div>
                  <div className="size-price">${size.price.toFixed(2)}</div>
                  {badge.text && (
                    <div className={`dpi-badge ${badge.className}`}>
                      {badge.text}
                    </div>
                  )}
                  {isBackorder ? (
                    <div className="backorder-badge">
                      <span className="badge-label">Backorder</span>
                      <span className="badge-date">Est. {getBackorderDate(size.id)}</span>
                    </div>
                  ) : (
                    <div className="in-stock-badge">
                      <span className="badge-label">In Stock</span>
                    </div>
                  )}
                  {isLowQuality && (
                    <div className="quality-warning">
                      <i className="fas fa-exclamation-triangle"></i> Lower quality at this size
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SizeSelectionStep;
